import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {TOAST_COLORS} from './toastColors'
import type {Toast} from './toastTypes'

interface ToastItemProps {
  toast: Toast
  onDismiss: (id: string) => void
}

const FADE_MS = 200

const ToastItem = ({toast, onDismiss}: ToastItemProps) => {
  const {t} = useTranslation()
  const [visible, setVisible] = useState(false)
  const [leaving, setLeaving] = useState(false)

  useEffect(() => {
    const enterId = window.setTimeout(() => setVisible(true), 10)
    const leaveId = window.setTimeout(() => setLeaving(true), Math.max(toast.durationMs - FADE_MS, 0))
    const dismissId = window.setTimeout(() => onDismiss(toast.id), toast.durationMs)
    return () => {
      window.clearTimeout(enterId)
      window.clearTimeout(leaveId)
      window.clearTimeout(dismissId)
    }
  }, [toast.id, toast.durationMs, onDismiss])

  const colors = TOAST_COLORS[toast.variant]
  const opacity = leaving ? 0 : visible ? 1 : 0
  const translateY = leaving ? 8 : visible ? 0 : 8

  const handleDismiss = () => {
    setLeaving(true)
    window.setTimeout(() => onDismiss(toast.id), FADE_MS)
  }

  return (
    <div
      role={toast.variant === 'error' ? 'alert' : 'status'}
      style={{
        ...styles.item,
        backgroundColor: colors.bg,
        color: colors.fg,
        borderLeft: `3px solid ${colors.accent}`,
        opacity,
        transform: `translateY(${translateY}px)`,
      }}
    >
      <span style={styles.message}>{toast.message}</span>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={t('toast.dismiss')}
        style={{...styles.dismiss, color: colors.fg}}
      >
        ×
      </button>
    </div>
  )
}

export default ToastItem

const styles = {
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 280,
    maxWidth: 480,
    padding: '10px 12px 10px 14px',
    borderRadius: 8,
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.45)',
    fontFamily: '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSize: 14,
    lineHeight: 1.4,
    backdropFilter: 'blur(8px)',
    transition: `opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease`,
    pointerEvents: 'auto',
  },
  message: {
    flex: 1,
    wordBreak: 'break-word',
  },
  dismiss: {
    appearance: 'none',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: 20,
    lineHeight: 1,
    padding: '2px 6px',
    opacity: 0.7,
  },
} as const
